import React from 'react';
import PropTypes from 'prop-types';
import BetButton from '../BetButton/';
import CountDownTimer from '../CountDownTimer';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { Trans } from 'react-i18next';

import SelectionColumn from '~shared/components/react/ThreeMarketBetCard/SelectionColumn';
import { getSelectionLabel } from '~shared/utils/selections-utils';
import {
    BET_CARD_BODY_CLASS_NAME,
    BET_CARD_DETAILS_CLASS_NAME,
    BET_CARD_TIMER_CLASS_NAME,
    BET_CARD_LABEL_CLASS_NAME,
    BET_CARD_SUSPENDED_CLASS_NAME,
    BET_CARD_SUSPENDED_EVENT_CLASS_NAME,
    BET_CARD_SUSPENDED_THREE_MARKET_CLASS_NAME,
    BET_CARD_BET_BUTTONS_CLASS_NAME,
    BET_CARD_LIVE_TAG_CLASS_NAME,
    BET_CARD_LIVE_TAG_TEXT,
    BET_CARD_SUSPENDED_TEXT,
    BET_CARD_FOOTER_CLASS_NAME,
} from './constants';
import './styles/bet-card.scss';

const propTypes = {
    selections: PropTypes.array.isRequired,
    description: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]).isRequired,
    timer: PropTypes.string,
    scoreBoard: PropTypes.object,
    showSelectionLabels: PropTypes.bool,
    betable: PropTypes.bool.isRequired,
    isLive: PropTypes.bool,
    gtmAttribute: PropTypes.string,
    event: PropTypes.shape({
        sport_slug: PropTypes.string,
    }),
    betCardFooterElement: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    usesThreeMarketView: PropTypes.bool,
    isUsFormat: PropTypes.bool,
    isBettedOnSelection: PropTypes.bool,
    marketOrder: PropTypes.array,
    markets: PropTypes.shape({
        market_id: PropTypes.shape({
            betable: PropTypes.bool,
            id: PropTypes.number,
            name: PropTypes.string,
            selection_schema: PropTypes.number,
            selections: PropTypes.object,
        }),
    }),
};

const defaultProps = {
    event: null,
    betCardFooterElement: null,
    usesThreeMarketView: false,
    isUsFormat: false,
    marketOrder: [],
    markets: {},
};

class BetCard extends React.Component {
    shouldComponentUpdate(nextProps) {
        return !isEqual(this.props, nextProps);
    }

    static generateTimer(time, isLive) {
        let content;

        if (isLive) {
            content = <div className={BET_CARD_LIVE_TAG_CLASS_NAME}><Trans>{BET_CARD_LIVE_TAG_TEXT}</Trans></div>;
        } else {
            content = <CountDownTimer scheduledTime={time} />;
        }

        return (
            <div className={BET_CARD_TIMER_CLASS_NAME}>
                {content}
            </div>
        );
    }

    render() {
        let selections;
        const { usesThreeMarketView = false, marketOrder = [], markets = {}, isUsFormat = false } = this.props;
        const timer = this.props.timer ? BetCard.generateTimer(this.props.timer, this.props.isLive) : null;
        const gtmBetButtonAttribute = `${this.props.gtmAttribute}`;

        if (this.props.betable) {
            selections = usesThreeMarketView
            // the market order is determined by the order of extra_default_market_id_1, extra_default_market_id_2, extra_default_market_id_3 in the 'sports' table of the DB.
            ? marketOrder.map(marketId =>
                <SelectionColumn
                    key={marketId}
                    isUsFormat={isUsFormat}
                    market={markets[marketId]}
                    gtmAttribute={gtmBetButtonAttribute}
                />)
            : this.props.selections.map((selection, key) => {
                const { is_extra_time: isExtraTime = false } = selection;
                const label = getSelectionLabel(selection);

                return (<BetButton
                    key={`${selection.id}-${key}`}
                    options={{...selection, label }}
                    showSelectionLabels={ isExtraTime || this.props.showSelectionLabels}
                    {
                        ...(isExtraTime && {
                            doTranslateLabel: false,
                        })
                    }
                    gtmAttribute={gtmBetButtonAttribute}
                />);
            });
        } else {
            const isSuspendedEventModifier = usesThreeMarketView
                                            ? Object.values(markets).some(({selections = {}}) => Object.values(selections).length > 0)
                                            : this.props.selections.length > 1;
            const suspendedText = isSuspendedEventModifier ? <Trans>{BET_CARD_SUSPENDED_TEXT}</Trans> : '-';

            const classModifier = classNames({
                [BET_CARD_SUSPENDED_EVENT_CLASS_NAME] : isSuspendedEventModifier,
                [BET_CARD_SUSPENDED_THREE_MARKET_CLASS_NAME] : usesThreeMarketView,
            });

            const dataSpan = usesThreeMarketView ? 3 : this.props.selections.length;
            selections = <div
                className={`${BET_CARD_SUSPENDED_CLASS_NAME} ${classModifier}`}
                data-span={dataSpan}>
                    {suspendedText}
                </div>;
        }

        const classBettedOnSelection = classNames('bet-card', {
            'bet-card--betted' : this.props.isBettedOnSelection,
        });
        const classThreeMarketViewBody = classNames({
            [`${BET_CARD_BODY_CLASS_NAME}--three_market_view`] : usesThreeMarketView,
        });
        const classThreeMarketViewLabel = classNames({
            [`${BET_CARD_LABEL_CLASS_NAME}--three_market_view`] : usesThreeMarketView,
        });

        return (
            <div className={classBettedOnSelection}>
                <div className={`${BET_CARD_BODY_CLASS_NAME} ${classThreeMarketViewBody}`}>
                    <div className={BET_CARD_DETAILS_CLASS_NAME}>
                        {timer}
                        <span className={`${BET_CARD_LABEL_CLASS_NAME} ${classThreeMarketViewLabel}`}>
                            {this.props.description}
                        </span>
                    </div>
                </div>
                <div className={BET_CARD_BET_BUTTONS_CLASS_NAME}>
                    {selections}
                </div>
                {this.props.betCardFooterElement && (
                    <div className={BET_CARD_FOOTER_CLASS_NAME}>
                        {this.props.betCardFooterElement}
                    </div>
                )}
            </div>
        );
    }
}

BetCard.propTypes = propTypes;
BetCard.defaultProps = defaultProps;

export default BetCard;
